import React, { FC, memo, useCallback, useState } from 'react';
import testIds from '@smart-tv/e2e/shared/variables';

import {
  DEFAULT_FAVORITES_LIST,
  FavoritesList
} from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';

import { DialogItem } from '@smart-tv/components/dialog';

import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';

import FavoritesHomeListButtons from './favorites-home-list-buttons';

import { FocusContext, FocusHandler, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import styles from '../favorites.module.scss';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
interface Props {
  isFocused: boolean;
  onFocus: FocusHandler;
  onBlur: FocusHandler;
}

export const FavoritesHomeList: FC<Props> = (props: Props) => {
  const { isFocused, onBlur, onFocus } = props;
  const { focusKey, ref } = useFocusable({ onBlur, onFocus });
  const { listID, usedFavoritesLists, handleSetInitialFavoriteList } = useFavoritesEdit();
  const { onSelectFavoritesList, selectedFavoritesList } = useFavoritesLists();

  const [errorListId, setErrorListId] = useState<number | null>(null);

  const { isOffline, setIsOffline, checkAndUpdateNetwork } = useNetwork();


  const handleSelectFavoriteListAction = useCallback(
    async (favoritesList: FavoritesList) => {
      const hasNetwork = await checkAndUpdateNetwork();
      if (hasNetwork) {
        await onSelectFavoritesList(favoritesList);
        setErrorListId(null);
      } else {
        setErrorListId(favoritesList.id);
      }
    },
    [onSelectFavoritesList, checkAndUpdateNetwork]
  );

  const retrySelectFavoriteListAction = useCallback(async () => {
    if (errorListId !== null && errorListId !== undefined) {
      const listToRetry = usedFavoritesLists?.find(
        (list: FavoritesList) => list.id === errorListId
      );
      if (listToRetry) {
        await handleSelectFavoriteListAction(listToRetry);
      }
    }
  }, [handleSelectFavoriteListAction, errorListId, usedFavoritesLists]);

  const closeNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
    setErrorListId(null);
  }, [setIsOffline, setErrorListId]);

  const handleFavoriteListFocus = useCallback(
    (favoritesList: FavoritesList) => {
      handleSetInitialFavoriteList(favoritesList.id);
    },
    [handleSetInitialFavoriteList]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['favorites-home-root--list']} ref={ref}>
        {usedFavoritesLists?.map((favoritesList) => (
          <div
            className={styles['favorites-home-root--row']}
            key={favoritesList.id}
            data-testid={testIds.favorites.home.listRow}
          >
            <div
              className={styles['favorites-home-root--item']}
              data-testid={testIds.favorites.home.listItem}
            >
              <DialogItem
                key={favoritesList.id}
                dataTestId={testIds.favorites.home.dialogItem}
                isFocused={favoritesList.id === selectedFavoritesList?.id}
                selected={favoritesList.id === selectedFavoritesList?.id}
                focusedContainer={
                  favoritesList.id === selectedFavoritesList?.id &&
                  favoritesList.id !== DEFAULT_FAVORITES_LIST.id
                }
                onEnterPress={() => {
                  handleSelectFavoriteListAction(favoritesList);
                  handleFavoriteListFocus(favoritesList);
                }}
                onFocus={() => handleFavoriteListFocus(favoritesList)}
              >
                {favoritesList.name}
              </DialogItem>
            </div>
            {isOffline && errorListId === favoritesList.id && (
              <NetworkErrorDialog
                onTryAgain={() => retrySelectFavoriteListAction()}
                onClose={closeNetworkErrorDialogAction}
              />
            )}

            {((isFocused && !IS_BROWSER && favoritesList.id === listID) ||
              (IS_BROWSER && favoritesList.id === selectedFavoritesList?.id)) &&
              favoritesList.id !== DEFAULT_FAVORITES_LIST.id && (
                <FavoritesHomeListButtons listId={favoritesList.id} />
              )}
          </div>
        ))}
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesHomeList);
